import LZString from './fp/compress.js';
import { RETRY, budgetRemaining, priorAttemptHeader, retryDecision } from './fp/post-retry.mjs';

/** Pino-like serializable plain object from any thrown value (name, message, stack, cause). */
export function serializeError(err) {
  if (err == null) return { message: 'null' };
  if (typeof err === 'object' && err !== null && typeof err.message !== 'undefined') {
    const o = {};
    if (err.name != null) o.name = String(err.name);
    if (err.message != null) o.message = String(err.message);
    if (err.stack != null) o.stack = String(err.stack);
    const c = err.cause;
    if (c != null) {
      try {
        o.cause =
          typeof c === 'object' && c !== null && typeof c.message !== 'undefined'
            ? serializeError(c)
            : { message: String(c) };
      } catch (e) {
        /* omit cause */
      }
    }
    return o;
  }
  return { message: String(err) };
}

export const RS_SESSION_COOKIE_NAME = 'rs_session_id';
export const RS_SESSION_COOKIE_DAYS = 30;

export function getCookie(name) {
  let cookie;
  try {
    cookie = typeof document !== 'undefined' ? document.cookie : '';
  } catch (e) {
    return null;
  }
  if (typeof cookie !== 'string') return null;
  const match = cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

export function setCookie(name, value, days) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  try {
    document.cookie =
      name + '=' + encodeURIComponent(value) + ';expires=' + expires.toUTCString() + ';path=/;SameSite=Lax';
  } catch (e) {
    /* ignore — session id is still returned by ensureRsSessionId */
  }
}

export function generateSessionId() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
}

let _memorySessionId = null;

/**
 * Point the session cookie and the in-memory id at a session the backend already has.
 * Used when `collect` skips because the last success is still inside 24h.
 * @param {string} id
 * @returns {string}
 */
export function pinRsSessionId(id) {
  if (typeof id !== 'string' || id.length === 0) return id;
  setCookie(RS_SESSION_COOKIE_NAME, id, RS_SESSION_COOKIE_DAYS);
  _memorySessionId = id;
  return id;
}

export function peekRsSessionId() {
  return getCookie(RS_SESSION_COOKIE_NAME) || _memorySessionId;
}

/** Read or create `rs_session_id` cookie (same as fingerprint collect). */
export function ensureRsSessionId() {
  const fromCookie = getCookie(RS_SESSION_COOKIE_NAME);
  if (fromCookie) {
    _memorySessionId = fromCookie;
    return fromCookie;
  }
  const id = generateSessionId();
  setCookie(RS_SESSION_COOKIE_NAME, id, RS_SESSION_COOKIE_DAYS);
  if (_memorySessionId && getCookie(RS_SESSION_COOKIE_NAME) !== id) return _memorySessionId;
  _memorySessionId = id;
  return id;
}

/** Origin-wide record of the last successful `rsSDK('collect')`. */
export const RS_LAST_COLLECT_KEY = 'rs_last_collect';

/** Collect again once this much time has passed since the stored success. */
export const COLLECT_INTERVAL_MS = 24 * 60 * 60 * 1000;

/**
 * Session id to reuse when `record` is still inside the interval.
 * Missing, corrupt, future, or expired records return null so collect runs.
 *
 * @param {unknown} record
 * @param {number} now epoch ms
 * @param {number} [intervalMs]
 * @returns {string | null}
 */
export function recentCollectSessionId(record, now, intervalMs = COLLECT_INTERVAL_MS) {
  if (!record || typeof record !== 'object') return null;
  const at = record.at;
  const sessionId = record.session_id;
  if (typeof sessionId !== 'string' || sessionId.length === 0) return null;
  if (typeof at !== 'number' || !Number.isFinite(at) || !Number.isFinite(now)) return null;
  if (at > now) return null;
  if (now - at >= intervalMs) return null;
  return sessionId;
}

function localStorageOrNull() {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch (e) {
    return null;
  }
}

/** @returns {unknown} Parsed record, or null when storage is missing, blocked, or corrupt. */
export function readLastCollect() {
  try {
    const ls = localStorageOrNull();
    if (!ls) return null;
    const raw = ls.getItem(RS_LAST_COLLECT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

/**
 * Persist a successful collect. Never throws.
 * @param {string} sessionId
 * @param {number} [now]
 * @returns {boolean} true when the write landed
 */
export function writeLastCollect(sessionId, now = Date.now()) {
  if (typeof sessionId !== 'string' || sessionId.length === 0) return false;
  const at = Number.isFinite(now) ? now : Date.now();
  try {
    const ls = localStorageOrNull();
    if (!ls) return false;
    ls.setItem(RS_LAST_COLLECT_KEY, JSON.stringify({ at: at, session_id: sessionId }));
    return true;
  } catch (e) {
    return false;
  }
}

/** `rsSDK('config')` options, without the application id. */
export function sdkConfigForCollect(options) {
  if (!options || typeof options !== 'object') return {};
  const config = {};
  const keys = Object.keys(options);
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (key === 'app_id' || key === 'appId') continue;
    config[key] = options[key];
  }
  return config;
}

/** Parse UUID v4 application id into fixed segments; return hex string for key derivation. */
export function appIdToSeed(appId) {
  if (!appId || typeof appId !== 'string') return null;
  const parts = appId.trim().split('-');
  if (parts.length === 5) {
    return (parts[0] + parts[2] + parts[4]).toLowerCase();
  }
  return appId.replace(/-/g, '').toLowerCase();
}

export function hexToBytes(hex) {
  if (hex.length % 2) hex = '0' + hex;
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16);
  return bytes;
}

export function bytesToBase64(u8) {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < u8.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, u8.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

export function base64ToBytes(b64) {
  const bin = atob(b64);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return u8;
}

/**
 * @param {{ endpoints?: string[], timeout?: number, debug?: boolean }} [options]
 * @param {string} defaultEndpoint no trailing slash
 */
export function getApiHost(options, defaultEndpoint) {
  const o = options || {};
  const list = o.endpoints;
  if (Array.isArray(list) && list.length > 0) {
    for (let i = 0; i < list.length; i++) {
      const url = typeof list[i] === 'string' ? list[i].trim() : null;
      if (url) return url.replace(/\/?$/, '');
    }
  }
  return String(defaultEndpoint).replace(/\/?$/, '');
}

export function invokeCallback(callback, result) {
  if (!callback) return;
  try {
    callback(result);
  } catch (e) {}
}

export function collectTimeoutMs(options) {
  return Number.isFinite(options?.timeout) ? options.timeout : 10000;
}

/** Wrap sync/async work, records duration in timings[name], preserves errors. */
export function withTiming(name, timings, fn) {
  const start = performance.now();

  try {
    const value = fn();
    if (value && typeof value.then === 'function') {
      return value.then(
        (resolved) => {
          timings[name] = performance.now() - start;
          return resolved;
        },
        (error) => {
          timings[name] = performance.now() - start;
          throw error;
        }
      );
    }

    timings[name] = performance.now() - start;
    return value;
  } catch (error) {
    timings[name] = performance.now() - start;
    throw error;
  }
}

/**
 * @param {string} base no trailing slash
 * @param {string} pathSegment e.g. collect or bb (no leading slash)
 * @param {string} appId
 */
export function sdkPostUrl(base, pathSegment, appId) {
  const path = String(pathSegment).replace(/^\//, '') + '/' + encodeURIComponent(appId.trim());
  return base + '/' + path;
}

/**
 * @param {string} msg
 * @param {'http'|'network'|'timeout'|'abort'} type
 * @param {{ status?: number, readyState?: number, responseUrl?: string|null, events: string[], elapsed_ms: number, target_url: string }} meta
 */
function postJsonError(msg, type, meta) {
  const err = new Error(msg);
  const failure = {
    type: type,
    status: meta.status,
    events: meta.events,
    elapsed_ms: meta.elapsed_ms,
    target_url: meta.target_url,
    ready_state: meta.readyState,
    response_url: meta.responseUrl ?? null,
  };
  const optional = [
    'first_progress_ms',
    'upload_bytes',
    'upload_done',
    'response_text',
    'content_type',
    'content_length',
  ];
  for (let i = 0; i < optional.length; i++) {
    if (meta[optional[i]] !== undefined) failure[optional[i]] = meta[optional[i]];
  }
  err.rsPostFailure = failure;
  return err;
}

const RESPONSE_TEXT_MAX = 500;

function truncateResponseText(text) {
  if (typeof text !== 'string') return null;
  let end = Math.min(text.length, RESPONSE_TEXT_MAX);
  const last = text.charCodeAt(end - 1);
  if (end < text.length && last >= 0xd800 && last <= 0xdbff) end -= 1;
  return text.slice(0, end);
}

function serverErrorMeta(text, header) {
  const contentLength = header('content-length');
  const length = contentLength ? Number(contentLength) : NaN;
  return {
    response_text: truncateResponseText(text),
    content_type: header('content-type') || null,
    content_length: isFinite(length) ? length : null,
  };
}

function serviceWorkerControlled() {
  try {
    return navigator.serviceWorker ? navigator.serviceWorker.controller != null : null;
  } catch (e) {
    return null;
  }
}

/** Page/environment snapshot for collect error diagnostics (no query string in page_url). */
export function collectPageContext() {
  const ctx = {};
  if (typeof window !== 'undefined' && window.location) {
    try {
      const u = new URL(window.location.href);
      ctx.page_url = u.origin + u.pathname;
    } catch (e) {
      ctx.page_url = null;
    }
  }
  if (typeof document !== 'undefined') {
    ctx.document_ready_state = document.readyState;
    ctx.visibility_state = document.visibilityState;
  }
  if (typeof navigator !== 'undefined') {
    ctx.online = navigator.onLine;
    ctx.sw_controlled = serviceWorkerControlled();
    const conn = navigator.connection;
    if (conn) {
      ctx.connection = {
        effective_type: conn.effectiveType ?? null,
        downlink: conn.downlink ?? null,
      };
    }
  }
  return ctx;
}

/**
 * Merge serializeError output with diagnostics.failure / diagnostics.context for server error reports.
 * @param {unknown} err
 * @param {Record<string, unknown>} [context]
 */
export function buildCollectErrorRecord(err, context) {
  const record = serializeError(err);
  let failure;
  if (err && typeof err === 'object' && err !== null && err.rsPostFailure && typeof err.rsPostFailure === 'object') {
    failure = Object.assign({}, err.rsPostFailure);
  } else {
    failure = { type: 'other' };
  }
  record.diagnostics = {
    failure: failure,
    context: context && typeof context === 'object' ? context : {},
  };
  if (err && typeof err === 'object') {
    if (Array.isArray(err.rsAttempts)) record.diagnostics.attempts = err.rsAttempts.slice();
    if (typeof err.rsRetries === 'number') record.diagnostics.retries = err.rsRetries;
  }
  return record;
}

/**
 * Fire-and-forget JSON POST via sendBeacon (no response; used when XHR error report fails).
 * @param {string} url
 * @param {object} body
 * @returns {boolean}
 */
export function beaconJsonPost(url, body) {
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
    return false;
  }
  try {
    const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
    return navigator.sendBeacon(url, blob);
  } catch (e) {
    return false;
  }
}

/**
 * Build the encrypted `error`-channel report payload for a collect/bb failure.
 * @param {string} appId
 * @param {unknown} err
 * @param {string|null} sessionId
 * @param {Record<string, unknown>} context
 */
async function buildSdkErrorReportPayload(appId, err, sessionId, context) {
  const record = buildCollectErrorRecord(err, context);
  const innerBody = {
    error: LZString.compressToBase64(JSON.stringify(record)),
    session_id: sessionId ?? null,
    version: typeof __SDK_VERSION__ !== 'undefined' && __SDK_VERSION__ != null ? __SDK_VERSION__ : null,
  };
  const payload = await encryptBody(appId, innerBody);
  return { payload, innerBodyBytes: JSON.stringify(innerBody).length, encryptionV: payload.v };
}

/**
 * Best-effort report of a client-side collect/bb failure to the server `error` channel.
 * Default: XHR, falling back to sendBeacon. With `preferBeacon` (bb auto-save, where a form submit
 * navigates right after collect): sendBeacon first (survives unload), falling back to XHR. Never throws.
 * @param {{ appId: string, url: string, timeoutMs: number, err: unknown, sessionId: string|null, reportContext?: Record<string, unknown>, failLabel?: string, preferBeacon?: boolean }} args
 */
export async function reportSdkCollectFailure({
  appId,
  url,
  timeoutMs,
  err,
  sessionId,
  reportContext,
  failLabel,
  preferBeacon,
}) {
  try {
    const baseContext = Object.assign({}, collectPageContext(), reportContext || {}, {
      endpoint: url,
      timeout_ms: timeoutMs,
    });

    const buildPayload = async (delivery) => {
      const context = Object.assign({}, baseContext, { delivery: delivery });
      const built = await buildSdkErrorReportPayload(appId, err, sessionId, context);
      context.encryption_v = built.encryptionV;
      context.body_bytes = built.innerBodyBytes;
      return (await buildSdkErrorReportPayload(appId, err, sessionId, context)).payload;
    };

    if (preferBeacon) {
      // Navigation-safe path: bb auto-save resumes the form submit right after collect resolves,
      // so an in-flight XHR would be aborted by the unload. sendBeacon is delivered during unload;
      // fall back to XHR only if the browser refuses to queue the beacon.
      if (beaconJsonPost(url, await buildPayload('beacon'))) return;
      try {
        await postJson(url, await buildPayload('xhr'), timeoutMs, failLabel || 'collect');
      } catch (e) {
        /* best-effort; ignore */
      }
      return;
    }

    try {
      await postJson(url, await buildPayload('xhr'), timeoutMs, failLabel || 'collect');
      return;
    } catch (e) {
      /* XHR report failed; try sendBeacon */
    }

    beaconJsonPost(url, await buildPayload('beacon'));
  } catch (e) {
    /* best-effort; ignore */
  }
}

/**
 * @param {string} url
 * @param {object} body
 * @param {number} timeoutMs
 * @param {string} [failLabel] prefix for Error message (default "request")
 * @returns {Promise<null>}
 */
export function postJson(url, body, timeoutMs, failLabel, opts) {
  const label = failLabel || 'request';
  const headers = (opts && opts.headers) || {};
  return new Promise(function (resolve, reject) {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    let settled = false;
    const startedAt = performance.now();
    const events = [];
    const upload = xhr.upload || null;
    let firstProgressMs = null;
    let uploadBytes = 0;
    let uploadDone = false;

    function fail(msg, type) {
      if (settled) return;
      settled = true;
      const meta = {
        status: xhr.status,
        readyState: xhr.readyState,
        responseUrl: xhr.responseURL || null,
        events: events.slice(),
        elapsed_ms: Math.round(performance.now() - startedAt),
        target_url: url,
      };
      if (upload) {
        meta.first_progress_ms = firstProgressMs;
        meta.upload_bytes = uploadBytes;
        meta.upload_done = uploadDone;
      }
      if (xhr.status >= 500) {
        try {
          Object.assign(
            meta,
            serverErrorMeta(xhr.responseText, function (name) {
              return xhr.getResponseHeader(name);
            })
          );
        } catch (e) {
          /* best-effort; ignore */
        }
      }
      reject(postJsonError(msg, type, meta));
    }

    xhr.onreadystatechange = function () {
      if (xhr.readyState !== XMLHttpRequest.DONE) return;
      events.push('onreadystatechange');
      if (xhr.status === 204) {
        if (settled) return;
        settled = true;
        resolve(null);
        return;
      }
      if (settled) return;
      if (xhr.status !== 0) {
        fail(label + ' failed: HTTP ' + xhr.status, 'http');
        return;
      }
      setTimeout(function () {
        fail(label + ' failed: network error', 'network');
      }, 0);
    };
    xhr.onerror = function () {
      events.push('onerror');
      fail(label + ' failed: network error', 'network');
    };
    xhr.onabort = function () {
      events.push('onabort');
      fail(label + ' failed: aborted', 'abort');
    };
    if (timeoutMs > 0) {
      xhr.timeout = timeoutMs;
      xhr.ontimeout = function () {
        events.push('ontimeout');
        fail(label + ' failed: timeout', 'timeout');
      };
    }
    if (upload) {
      upload.onprogress = function (e) {
        if (firstProgressMs === null) firstProgressMs = Math.round(performance.now() - startedAt);
        if (e && e.loaded > uploadBytes) uploadBytes = e.loaded;
        if (e && e.lengthComputable && e.loaded >= e.total) uploadDone = true;
      };
      upload.onload = function () {
        uploadDone = true;
      };
    }
    xhr.setRequestHeader('X-Request-Started-At', String(Date.now()));
    const names = Object.keys(headers);
    for (let i = 0; i < names.length; i++) xhr.setRequestHeader(names[i], headers[names[i]]);
    xhr.send(JSON.stringify(body));
  });
}

export const KEEPALIVE_MAX_BODY = 60000;

const KEEPALIVE_DRAIN_MAX_MS = 2000;

export function supportsKeepalive() {
  try {
    return typeof fetch === 'function' && typeof Request === 'function' && 'keepalive' in Request.prototype;
  } catch (e) {
    return false;
  }
}

export function postJsonKeepalive(url, json, timeoutMs, failLabel, headers) {
  const label = failLabel || 'request';
  const startedAt = performance.now();
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const meta = (status, events) => ({
    status: status,
    readyState: 4,
    responseUrl: null,
    events: events,
    elapsed_ms: Math.round(performance.now() - startedAt),
    target_url: url,
  });
  return new Promise(function (resolve, reject) {
    let settled = false;
    let timer = null;
    let httpFailure = null;
    const finish = function (fn, value) {
      if (settled) return;
      settled = true;
      if (timer !== null) clearTimeout(timer);
      fn(value);
    };
    if (timeoutMs > 0) {
      timer = setTimeout(function () {
        finish(
          reject,
          httpFailure
            ? httpFailure()
            : postJsonError(label + ' failed: timeout', 'timeout', meta(0, ['fetch', 'timeout']))
        );
        if (controller) controller.abort();
      }, timeoutMs);
    }
    let request;
    try {
      request = fetch(url, {
        method: 'POST',
        body: json,
        keepalive: true,
        headers: Object.assign(
          { 'Content-Type': 'application/json', 'X-Request-Started-At': String(Date.now()) },
          headers || {}
        ),
        signal: controller ? controller.signal : undefined,
      });
    } catch (e) {
      finish(reject, postJsonError(label + ' failed: network error', 'network', meta(0, ['fetch', 'throw'])));
      return;
    }
    request.then(
      function (res) {
        if (res.status === 204) {
          finish(resolve, null);
          return;
        }
        const answered = meta(res.status, ['fetch']);
        const serverError = res.status >= 500;
        let text = null;
        httpFailure = function () {
          const failureMeta = serverError
            ? Object.assign(
                answered,
                serverErrorMeta(text, function (name) {
                  return res.headers.get(name);
                })
              )
            : answered;
          return postJsonError(label + ' failed: HTTP ' + res.status, 'http', failureMeta);
        };
        const failHttp = function () {
          clearTimeout(drainTimer);
          finish(reject, httpFailure());
        };
        const drainTimer = setTimeout(failHttp, KEEPALIVE_DRAIN_MAX_MS);
        const drain = serverError
          ? res.text().then(function (body) {
              text = body;
            })
          : res.arrayBuffer();
        drain.then(failHttp, failHttp);
      },
      function () {
        finish(reject, postJsonError(label + ' failed: network error', 'network', meta(0, ['fetch', 'onerror'])));
      }
    );
  });
}

function waitMs(ms) {
  return new Promise(function (resolve) {
    setTimeout(resolve, ms);
  });
}

export function postWithRetry(url, payload, timeoutMs, failLabel, opts) {
  const keepaliveJson = opts && typeof opts.keepaliveJson === 'string' ? opts.keepaliveJson : null;
  const onXhrRetry = opts && typeof opts.onXhrRetry === 'function' ? opts.onXhrRetry : null;
  const startedAt = performance.now();
  const attempts = [];

  function remainingMs() {
    return budgetRemaining(startedAt, performance.now(), timeoutMs);
  }

  function attempt(retriesDone, keepalive, prior) {
    const remaining = remainingMs();
    const attemptTimeoutMs = remaining === Infinity ? 0 : Math.max(1, Math.round(remaining));
    const headers = {
      'X-Request-Retries': String(retriesDone),
      'X-Request-Prior-Attempt': prior ? priorAttemptHeader(prior) : '-',
    };
    const send = keepalive
      ? postJsonKeepalive(url, keepaliveJson, attemptTimeoutMs, failLabel, headers)
      : postJson(url, payload, attemptTimeoutMs, failLabel, { headers: headers });
    return send.then(
      function () {
        return { retries: retriesDone };
      },
      function (err) {
        const failure = err && err.rsPostFailure ? err.rsPostFailure : null;
        if (failure) attempts.push(failure);
        const giveUp = function () {
          if (err && typeof err === 'object') {
            err.rsAttempts = attempts.slice();
            err.rsRetries = retriesDone;
          }
          throw err;
        };
        const decision = retryDecision(failure, {
          retriesDone: retriesDone,
          remainingMs: remainingMs(),
          noPause: keepalive,
        });
        if (!decision.retry) return giveUp();
        const nextKeepalive = keepalive && failure.type !== 'network';
        return waitMs(decision.delayMs).then(function () {
          if (remainingMs() < RETRY.minAttemptMs) return giveUp();
          if (keepalive && !nextKeepalive && onXhrRetry) onXhrRetry();
          return attempt(retriesDone + 1, nextKeepalive, failure);
        });
      }
    );
  }

  return attempt(0, keepaliveJson !== null, null);
}

/** Derive AES-256 key from application id (segments 0,2,4 of UUID). Returns null if crypto.subtle unavailable. */
export async function deriveAesKey(appId, salt, usages) {
  if (typeof crypto === 'undefined' || !crypto.subtle) return null;
  const seed = appIdToSeed(appId);
  if (!seed || !/^[0-9a-f]+$/i.test(seed)) return null;
  const seedBytes = hexToBytes(seed);
  const saltBytes = salt ? new TextEncoder().encode(salt) : new Uint8Array(0);
  const material = new Uint8Array(seedBytes.length + saltBytes.length);
  material.set(seedBytes);
  material.set(saltBytes, seedBytes.length);
  const hash = await crypto.subtle.digest('SHA-256', material);
  return crypto.subtle.importKey('raw', hash, { name: 'AES-GCM', length: 256 }, false, usages);
}

export function deriveEncryptionKey(appId) {
  return deriveAesKey(appId, '', ['encrypt']);
}

/**
 * Encrypt request body with AES-GCM; returns { v: 1, iv, d }. When crypto.subtle unavailable, returns { v: 0, ... }.
 * @param {string} appId
 * @param {{ session_id?: string|null, sdk?: string, error?: string, bb?: string, version?: string|null, config?: object, features?: { fp: boolean, di: boolean } }} body
 */
export async function encryptBody(appId, body) {
  const key = await deriveEncryptionKey(appId);
  if (!key) {
    const out = { v: 0, session_id: body.session_id ?? null };
    if (body.sdk != null) out.sdk = body.sdk;
    if (body.error != null) out.error = body.error;
    if (body.bb != null) out.bb = body.bb;
    if (body.version != null) out.version = body.version;
    if (body.config != null) out.config = body.config;
    if (body.features != null) out.features = body.features;
    return out;
  }
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plain = new TextEncoder().encode(JSON.stringify(body));
  const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv, tagLength: 128 }, key, plain);
  return {
    v: 1,
    iv: bytesToBase64(iv),
    d: bytesToBase64(new Uint8Array(cipher)),
  };
}
